import React from 'react';
import { useStore } from '../context/StoreContext';
import { CoveristicLogo } from './CoveristicLogo';
import { 
  MessageCircle, 
  Shield, 
  Truck, 
  Sparkles, 
  Smartphone, 
  Palette, 
  Shirt
} from 'lucide-react';
import {
  getProductLocalizedTitle,
  translateEnglishTextToArabic,
  getCategoryPermalink
} from '../utils/localization';

export const HeroBanner: React.FC = () => {
  const { pagesContent, products, setCurrentView, setActiveCategoryFilter, setSelectedProduct, language, t } = useStore();
  const { hero } = pagesContent;

  const handleShopCategory = (cat: string) => {
    setSelectedProduct(null);
    setActiveCategoryFilter(cat);
    setCurrentView('shop');
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  // Resolve 3 showcase cards dynamically: custom admin showcase items OR auto-sync to products (mixing classic favorites and new releases)
  const activeProducts = (products || []).filter(p => p && !p.trashedAt && p.inStock !== false);

  const phoneCasesList = activeProducts.filter(p => p.category === 'phone-cases');
  const canvasList = activeProducts.filter(p => p.category === 'canvas');
  const mugList = activeProducts.filter(p => p.category === 'mugs');

  // Blend old and new: show a brand new release alongside established classic staples
  const phoneProduct = phoneCasesList[0] || activeProducts[0];
  const canvasProduct = (canvasList.length > 1 ? canvasList[canvasList.length - 1] : canvasList[0]) || (activeProducts[1] && activeProducts[1].id !== phoneProduct?.id ? activeProducts[1] : activeProducts[0]);
  const mugProduct = (mugList.length > 1 ? mugList[Math.floor(mugList.length / 2)] : mugList[0]) || (activeProducts[2] && activeProducts[2].id !== phoneProduct?.id && activeProducts[2].id !== canvasProduct?.id ? activeProducts[2] : activeProducts[0]);

  const showcaseCards = (hero.showcaseItems && hero.showcaseItems.length === 3 ? hero.showcaseItems : [
    {
      category: 'phone-cases',
      title: phoneProduct ? getProductLocalizedTitle(phoneProduct, language) : (language === 'ar' ? 'كفرات هواتف مخصصة' : 'Phone Cases'),
      priceText: phoneProduct
        ? (language === 'ar' ? `ابتداءً من $${phoneProduct.price}` : `From $${phoneProduct.price}`)
        : (language === 'ar' ? 'ابتداءً من $10' : 'From $10'),
      image: phoneProduct?.image || '/images/catalog/img_01.webp'
    },
    {
      category: 'canvas',
      title: canvasProduct ? getProductLocalizedTitle(canvasProduct, language) : (language === 'ar' ? 'لوحات كانفاس جدارية' : 'Wall Canvas'),
      priceText: canvasProduct
        ? (language === 'ar' ? `ابتداءً من $${canvasProduct.price}` : `From $${canvasProduct.price}`)
        : (language === 'ar' ? 'ابتداءً من $20' : 'From $20'),
      image: canvasProduct?.image || '/images/catalog/img_02.webp'
    },
    {
      category: 'mugs',
      title: mugProduct ? getProductLocalizedTitle(mugProduct, language) : (language === 'ar' ? 'أكواب سيراميك مطبوعة' : 'Photo Mugs'),
      priceText: mugProduct
        ? (language === 'ar' ? `ابتداءً من $${mugProduct.price}` : `From $${mugProduct.price}`)
        : (language === 'ar' ? 'ابتداءً من $8' : 'From $8'),
      image: mugProduct?.image || '/images/catalog/img_04.webp'
    }
  ]).map(item => ({
    ...item,
    title: language === 'ar' ? translateEnglishTextToArabic(item.title, item.category) : item.title,
    priceText: language === 'ar' ? item.priceText.replace(/^From\s+/i, 'ابتداءً من ') : item.priceText
  }));

  return (
    <section 
      id="hero-banner-section"
      className="relative overflow-hidden border-b border-[#B7151D] bg-[#1a0507]"
      style={{
        backgroundColor: '#E21B24',
      }}
    >
      {/* Background layer: Hero image coveristic.webp + 80% overlay without pattern */}
      <div 
        className="absolute inset-0 pointer-events-none overflow-hidden"
      >
        <picture>
          <source media="(max-width: 640px)" srcSet="/hero-image-coveristic-mobile.webp" type="image/webp" />
          <source srcSet="/hero-image-coveristic.webp" type="image/webp" />
          <img
            src="/hero-image-coveristic.webp"
            alt="Coveristic Custom Print on Demand Studio"
            width="1920"
            height="600"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            className="w-full h-full object-cover object-center select-none pointer-events-none"
            referrerPolicy="no-referrer"
          />
        </picture>
        {/* Red overlay at 80% opacity (#E21B24 at 0.80) */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundColor: 'rgba(226, 27, 36, 0.80)',
          }}
        />
      </div>

      {/* Selected Element: div:nth-of-type(2) */}
      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 relative z-10 text-white"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          
          {/* Left Column: Custom Print on Demand Studio Headline & WhatsApp Concept */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            
            {/* Eyebrow badge */}
            <div className="inline-flex items-center gap-2 bg-neutral-950/80 border border-white/20 px-3 py-1 rounded-full text-xs font-bold tracking-widest text-white uppercase shadow-md">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
              <span>{language === 'ar' ? '★ استوديو الطباعة حسب الطلب بلبنان' : '★ CUSTOM PRINT ON DEMAND STUDIO'}</span>
            </div>

            {/* Main Headline (Single Primary H1 for Storefront) */}
            <h1 
              id="main-storefront-h1"
              className="text-4xl sm:text-5xl lg:text-6xl font-black italic tracking-tight leading-[1.05] uppercase text-white drop-shadow-md"
            >
              {language === 'ar' ? (
                <>
                  كفرات موبايل مخصصة، لوحات كانفاس وطباعة حسب الطلب في لبنان
                </>
              ) : (
                <>
                  CUSTOM PHONE CASES, CANVAS WALL ART &amp; PRINT ON DEMAND LEBANON
                </>
              )}
            </h1>

            {/* Subheadline description */}
            <p className="text-white/95 text-sm sm:text-base max-w-2xl leading-relaxed font-medium drop-shadow-xs">
              {language === 'ar'
                ? 'حوّل صورك المفضلة، لوحاتك، صور الحيوانات الأليفة ولوغو شركتك لكفرات موبايل لجميع الموديلات، كانفاس جداري، كاسات فخارية، تيشيرتات، هوديات وستيكرات. ابعت صورتك ع الواتساب ومعاينة البروفة متوفرة قبل الطباعة!'
                : 'Customize your favorite memories, personal artwork, pet portraits, and logos onto Phone Cases for all brands, Wall Art Canvas, Ceramic Mugs, T-Shirts, Hoodies & Stickers. Send your photo to our WhatsApp — image preview before printing request is available before printing!'
              }
            </p>

            {/* 3-Step WhatsApp How it Works Banner */}
            <div className="w-full bg-neutral-950/95 border border-neutral-800 rounded-xl p-4 sm:p-5 text-white shadow-2xl backdrop-blur-xs">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2.5 mb-3 flex-wrap gap-2">
                <span className="text-xs font-black uppercase tracking-wider text-neutral-200 flex items-center gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
                  {language === 'ar' ? 'كيف تطلب منتجك بالطباعة المخصصة؟' : 'How Custom Print Ordering Works'}
                </span>
                <span id="hero-payment-badge" className="text-[11px] font-bold text-emerald-400 bg-emerald-950/80 px-2.5 py-0.5 rounded border border-emerald-800/60">
                  {language === 'ar' ? 'الدفع نقداً عند الاستلام بالدولار ($ USD) أو عبر Whish Money' : 'Pay in Cash on Delivery ($ USD) or Whish Money'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center sm:text-left">
                <div className="space-y-1">
                  <div className="text-[#E21B24] font-black text-xs sm:text-sm">
                    {language === 'ar' ? '٠١. اختر المنتج' : '01. PICK YOUR ITEM'}
                  </div>
                  <div className="text-[11px] text-neutral-300 leading-tight">
                    {language === 'ar' ? 'غطاء هاتف، لوحة كانفاس، كوب، هودي، تيشيرت أو ملصقات.' : 'Phone case, canvas, mug, hoodie, tee or sticker.'}
                  </div>
                </div>
                <div className="space-y-1 border-x border-neutral-800 px-2">
                  <div className="text-[#E21B24] font-black text-xs sm:text-sm">
                    {language === 'ar' ? '٠٢. أرسل صورتك' : '02. SEND YOUR PHOTO'}
                  </div>
                  <div className="text-[11px] text-neutral-300 leading-tight">
                    {language === 'ar' ? 'أرسل صورتك عبر واتساب مباشرة إلى +961 81 773 349.' : 'Send your picture on WhatsApp to +961 81773349.'}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[#E21B24] font-black text-xs sm:text-sm">
                    {language === 'ar' ? '٠٣. اعتمد البروفة واستلم' : '03. APPROVE & DELIVER'}
                  </div>
                  <div className="text-[11px] text-neutral-300 leading-tight">
                    {language === 'ar' ? 'نرسل لك بروفة للموافقة، ثم نطبع ونوصل لكافة مناطق لبنان.' : 'Image preview request available, printed & shipped across Lebanon.'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: High-Impact Urban Visual Showcase */}
          <div className="lg:col-span-5 relative">
            {/* Visual Box Frame */}
            <div className="relative bg-white rounded-2xl p-4 shadow-2xl border border-neutral-200/20 text-neutral-900">
              
              {/* Top Banner Tag */}
              <div className="flex items-center justify-between pb-3 mb-2 border-b border-neutral-100">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#E21B24]"></div>
                  <span className="text-[11px] font-black uppercase tracking-widest text-neutral-800">
                    {language === 'ar' ? 'معرض المطبوعات والتصاميم' : 'CUSTOM PRINT SHOWCASE'}
                  </span>
                </div>
                <span className="text-[10px] font-bold bg-[#E21B24] text-white px-2 py-0.5 rounded uppercase">
                  {language === 'ar' ? 'اطبع صورتك' : 'PRINT YOUR IMAGE'}
                </span>
              </div>

              {/* 3-Part Image Montage with pure white backgrounds - synced to recently added products or custom showcase */}
              <div className="grid grid-cols-3 gap-2">
                {showcaseCards.map((card, idx) => (
                  <a 
                    key={idx}
                    href={getCategoryPermalink(card.category || 'phone-cases', language)}
                    onClick={(e) => {
                      if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
                        e.preventDefault();
                        handleShopCategory(card.category || 'phone-cases');
                      }
                    }}
                    className="group cursor-pointer bg-neutral-50 rounded-lg p-2 border border-neutral-200 hover:border-[#E21B24] transition-all text-center flex flex-col justify-between"
                  >
                    <div className="relative h-28 sm:h-32 flex items-center justify-center overflow-hidden bg-white rounded-md mb-2 select-none">
                      <div 
                        className="image-shield" 
                        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onDragStart={(e) => e.preventDefault()}
                      />
                      <img
                        src={card.image}
                        alt={card.title}
                        width="150"
                        height="150"
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onDragStart={(e) => e.preventDefault()}
                        className="h-full object-contain group-hover:scale-105 transition-transform duration-300 pointer-events-none select-none"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 150 150"><rect fill="%23f5f5f5" width="150" height="150"/><text fill="%23a3a3a3" font-family="sans-serif" font-size="11" font-weight="bold" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Coveristic</text></svg>';
                        }}
                      />
                    </div>
                    <div>
                      <span className="text-[10px] font-black uppercase text-neutral-900 block leading-tight truncate px-1" title={card.title}>
                        {card.title}
                      </span>
                      <span className="text-[10px] font-bold text-[#E21B24] block mt-0.5">
                        {card.priceText}
                      </span>
                    </div>
                  </a>
                ))}
              </div>

              {/* Bottom Card Footer */}
              <div className="mt-3 pt-3 border-t border-neutral-100 flex items-center justify-between text-xs">
                <span className="font-semibold text-neutral-600 flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-[#E21B24]" />
                  {language === 'ar' ? 'تصميمك الخاص • بروفة مجانية قبل الطباعة' : 'Custom Image • Preview Before Printing Available'}
                </span>
                <span className="font-black text-[#E21B24] uppercase tracking-wide">
                  {language === 'ar' ? 'الطلب عبر واتساب' : 'Order on WhatsApp'}
                </span>
              </div>
            </div>

            {/* Decorative Floating Street Sticker */}
            <div className="hidden sm:block absolute -bottom-5 -right-4 bg-[#E21B24] text-white font-black text-xs px-4 py-2 uppercase tracking-widest transform rotate-3 shadow-xl border-2 border-white">
              {language === 'ar' ? '★ صُنع مخصص حسب الطلب 100%' : '★ 100% CUSTOM MADE'}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
